import React, { useMemo, useRef, useState } from 'react';
// @ts-ignore
import PanoRichText from 'pano-rich-text';
import styled from 'styled-components';
import { formatSize } from 'pano-utils';
import { useHover } from 'react-use';
import { API_DOMAIN, MEDIA_DOMAIN, THUMB_SIZE_LIMIT } from '@constants';
import { ImageType, type LibItemArticle } from '@types';
import { queryClient, queryKeys } from '@queryClient';
import Modal from '@components/Modal';
import Title from '@components/Title';
import Button from '@components/Button';
import { getMediaUrl } from '@utils/urlMakers';
import Input from '@components/Input';
import TextLink from '@components/TextLink';
import Image from '@components/Image';
import IconUpload from '@components/icons/IconUpload';
import { $member } from '@stores/memberStores';
import selectFile from '@utils/selectFile';
import { sendError, sendSuccess, sendWarning } from '@components/Noti';
import Tip from '@components/Tip';
import useInput from '@hooks/useInput';
import axios from '@utils/axios';
import { selectLibArticle, selectLibImages, selectLibVideos } from '../index';
import { uploadThumb } from '../../alioss';
import { teamConfig } from '../../../utils/teamConfig';

type Props = {
  defaultData?: LibItemArticle;
  onRequestClose: () => void;
};

const StyledModal = styled(Modal)`
  width: 1000px;
  height: calc(100% - 60px);
`;

export const EDITOR_TOOL_BAR = [
  { name: 'styles', groups: ['styles'] },
  { name: 'colors', groups: ['colors'] },
  { name: 'basicstyles', groups: ['cleanup', 'basicstyles'] },
  { name: 'insert', groups: ['insert'] },
  {
    name: 'paragraph',
    groups: ['align', 'list', 'indent', 'blocks', 'bidi', 'paragraph'],
  },
  { name: 'links', groups: ['links'] },
  {
    name: 'editing',
    groups: ['find', 'selection', 'spellchecker', 'editing'],
  },
  { name: 'document', groups: ['mode', 'document', 'doctools'] },
  { name: 'clipboard', groups: ['clipboard', 'undo'] },
  { name: 'forms', groups: ['forms'] },
  { name: 'tools', groups: ['tools'] },
  { name: 'others', groups: ['others'] },
  { name: 'about', groups: ['about'] },
];

export const EDITOR_TOOL_REMOVE =
  'Save,Print,Preview,NewPage,Templates,Cut,Copy,Paste,PasteText,PasteFromWord,Undo,Redo,Find,Replace,SelectAll,Scayt,Form,Checkbox,Radio,TextField,Textarea,Select,Button,ImageButton,HiddenField,BulletedList,NumberedList,Blockquote,CreateDiv,BidiLtr,BidiRtl,Language,Anchor,Flash,Smiley,SpecialChar,PageBreak,Maximize,ShowBlocks,About,RemoveFormat,Strike,Table,Styles,Font,Source,Format,CopyFormatting';

const D_PLACEHOLDER = '__720YUN_DOMAIN_PLACEHOLDER_MEDIA__';

const LENGTH = 50;

const reReplace = new RegExp(MEDIA_DOMAIN, 'g');
const reRestore = new RegExp(D_PLACEHOLDER, 'g');

export const domainReplace = (str: string) => {
  return str.replace(reReplace, D_PLACEHOLDER);
};
export const domainRestore = (str: string) => {
  return str.replace(reRestore, MEDIA_DOMAIN);
};

const sendNoti = {
  error: sendError,
  warning: sendWarning,
  success: sendSuccess,
  info: sendSuccess,
};

function ArticleModal({ defaultData, onRequestClose }: Props) {
  const editor = useRef();
  const editorContent = useMemo(
    () => domainRestore(defaultData?.text ?? ''),
    [defaultData?.text]
  );
  const [thumbSrc, setThumbSrc] = useState(defaultData?.thumb ?? '');
  const input = useInput<HTMLInputElement>(defaultData?.title ?? '');

  const loading = false;

  const handleUploadClick = async ({
    type,
    uploadSuccessCb,
  }: {
    type: 'image' | 'html5video';
    uploadSuccessCb: (src: string) => void;
  }) => {
    if (type === 'image') {
      const { libItems } = await selectLibImages();
      if (!libItems) return;
      uploadSuccessCb(getMediaUrl(libItems[0].url));
    } else if (type === 'html5video') {
      const { libItems } = await selectLibVideos();
      if (!libItems) return;
      uploadSuccessCb(getMediaUrl(libItems[0].url));
    }
  };

  const addThumb = async () => {
    const files = await selectFile({
      accept: 'image/jpeg, image/png',
    });
    const file = files[0];
    if (file.size > THUMB_SIZE_LIMIT) {
      sendWarning(`图片过大（不能大于${formatSize(THUMB_SIZE_LIMIT)})`);
      return;
    }
    const { property } = $member.getState();
    const prefix = `/media/article/${property?.modHashId}/${property?.uid}`;
    const url = await uploadThumb({ file, prefix });
    setThumbSrc(url);
  };

  const handleConfirm = async () => {
    // @ts-ignore
    const htmlString = editor.current?.getData() || '';
    const msgs: string[] = [];
    if (!input.value.trim()) msgs.push('请输入文章标题');
    if (!htmlString) msgs.push('请输入文章内容');
    if (msgs.length) {
      msgs.forEach((msg) => sendWarning(msg));
      return;
    }
    const data = {
      title: input.value.trim(),
      text: domainReplace(htmlString),
      thumb: thumbSrc,
    };
    try {
      if (defaultData) {
        const url = teamConfig(
          `${API_DOMAIN}/member/my/media/article/${defaultData.id}`,
          `${API_DOMAIN}/team/app/tours/media/article/${defaultData.id}`
        );
        await axios.post(url, data);
        await queryClient.refetchQueries([
          queryKeys.libItemArticle,
          defaultData.id,
        ]);
      } else {
        const url = teamConfig(
          `${API_DOMAIN}/member/my/media/article`,
          `${API_DOMAIN}/team/app/tours/media/article`
        );
        await axios.post(url, data);
      }
    } catch (e) {
      sendError(e.message);
    }
    onRequestClose();
    selectLibArticle({ multiple: true });
  };

  const [btn] = useHover((isHovered) => (
    <div
      className="ml-10 flex h-40 w-40 cursor-pointer items-center justify-center bg-white-light"
      onClick={addThumb}
    >
      <IconUpload scheme="light" active={isHovered} />
    </div>
  ));

  const [cover] = useHover((isHovered) => (
    <div className="relative ml-10 flex  h-40 w-40 items-center justify-center bg-white-light">
      <Image
        className="h-full w-full"
        src={thumbSrc}
        type={ImageType.thumb}
        size={40}
      />
      {isHovered && (
        <div
          className="absolute flex h-30 w-30 cursor-pointer items-center justify-center rounded bg-black-black bg-opacity-50 hover:bg-blue hover:bg-opacity-100"
          onClick={addThumb}
        >
          <IconUpload color="#fff" />
        </div>
      )}
    </div>
  ));

  return (
    <StyledModal
      isOpen
      header={<Title>文章编辑</Title>}
      footer={
        <Button disabled={loading} onClick={handleConfirm}>
          确认操作
        </Button>
      }
      shouldCloseOnOverlayClick={false}
      onRequestClose={() => {
        onRequestClose();
        selectLibArticle({ multiple: true });
      }}
      backdropZIndex={8000}
    >
      <div className="absolute inset-0 flex flex-col">
        <div className="my-20 flex items-center">
          <div className="mr-20">文章标题</div>
          <Input
            className="h-40 flex-auto"
            maxLength={LENGTH}
            placeholder={`请输入文章标题，不超过${LENGTH}个字符`}
            onClear={input.clear}
            {...input.bind}
          />
          <div className="ml-20 flex flex-col">
            <div className="flex items-center">
              <div>文章封面</div>
              {!!thumbSrc && (
                <TextLink
                  className="ml-5 text-sm"
                  onClick={() => setThumbSrc('')}
                >
                  删除
                </TextLink>
              )}
            </div>
            <Tip>{`512*512   ${formatSize(THUMB_SIZE_LIMIT)}以内`}</Tip>
          </div>
          {thumbSrc ? cover : btn}
        </div>
        <PanoRichText
          className="flex flex-1 flex-col"
          handleNotification={sendNoti}
          handleUpload={handleUploadClick}
          noFile
          initValue={editorContent}
          toolbarGroup={EDITOR_TOOL_BAR}
          removeItem={EDITOR_TOOL_REMOVE}
          editorRef={(x: any) => {
            editor.current = x;
          }}
        />
      </div>
    </StyledModal>
  );
}

export default ArticleModal;
